import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { subDays } from 'date-fns';
import { getSchoolYearTippingPoint } from './schoolyear-tipping-point.js';

/**
 * Returns the school year for a given date
 * @param date The date to get the school year for. Defaults to the current date
 * @param yearOffset The offset to add to the year. Defaults to 0. With Offset 1 you can get the next school year
 * @returns The school year of the given date or now.
 */
export function getSchoolYear(options?: { date?: Date; yearOffset?: number }): string {
  const { date = new Date(), yearOffset = 0 } = options ?? {};
  // Instants ("now", legacy local-midnight values) are read as the German calendar day; canonical
  // day values pass through unchanged. From here on everything is on the canonical UTC grid.
  const day = toCalendarDay(date);
  const year = day.getUTCFullYear() + yearOffset;
  const startOfNewSchoolYear = getSchoolYearTippingPoint(year);

  const getSchoolYearString = (year: number) => year.toString().slice(2);

  // depending on which half of the year the quittung was made, the school year is different
  if (day < startOfNewSchoolYear) {
    return `${getSchoolYearString(year - 1)} / ${getSchoolYearString(year)}`;
  } else {
    return `${getSchoolYearString(year)} / ${getSchoolYearString(year + 1)}`;
  }
}

/**
 * Returns the start and end date of a school year string in the format "09 / 10"
 * @param schoolYear The school year to get the start and end date for must be in the format "09 / 10"
 * @returns The start and end date of the school year
 */
export function getSchoolYearIntervalDates(schoolYear: string): { start: Date | null; end: Date | null } {
  const cleanedString = schoolYear.replace(' ', '').replace('_', '/').split('/');
  if (!cleanedString[0] || !cleanedString[1]) {
    return { start: null, end: null };
  }
  let firstYear = cleanedString[0].trim();
  let secondYear = cleanedString[1].trim();

  // pad the string with the millenial if it is missing
  firstYear = firstYear.length === 2 ? `20${firstYear}` : firstYear;
  secondYear = secondYear.length === 2 ? `20${secondYear}` : secondYear;
  firstYear = firstYear.length === 3 ? `2${firstYear}` : firstYear;
  secondYear = secondYear.length === 3 ? `2${secondYear}` : secondYear;

  const numberStartYear = parseInt(firstYear, 10);
  const numberEndYear = parseInt(secondYear, 10);

  // check nan
  if (Number.isNaN(numberStartYear) || Number.isNaN(numberEndYear)) {
    return { start: null, end: null };
  }

  // check if the numbers are next to each other so we actually have only one year
  if (numberStartYear + 1 !== numberEndYear) {
    return { start: null, end: null };
  }

  return {
    start: getSchoolYearTippingPoint(numberStartYear),

    // start and end are inclusive calendar days: the end is 31 July, the day before the next tipping point
    end: subDays(getSchoolYearTippingPoint(numberEndYear), 1),
  };
}

export function getSchoolYearIntervalDatesFromDate(date: Date): { start: Date | null; end: Date | null } {
  const schoolYear = getSchoolYear({ date });
  return getSchoolYearIntervalDates(schoolYear);
}
